import { useState, useCallback } from 'react';
import { useOidcContext } from '../contexts/OidcContext';
import { api } from '../api';
import { ServerUserOut as ServerUser } from '../types/apiRequests';

export function useServerUsers(
  serverName: string | undefined,
  isBeheerder: boolean,
  onMutate?: () => void,
) {
  const { getAccessTokenSilently } = useOidcContext();
  const [serverUsers, setServerUsers] = useState<ServerUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [togglingUser, setTogglingUser] = useState<string | null>(null);

  const loadServerUsers = useCallback(async () => {
    if (!serverName || !isBeheerder) return;
    setUsersLoading(true);
    try {
      const token = await getAccessTokenSilently();
      setServerUsers(await api.getServerUsers(token, serverName));
    } finally {
      setUsersLoading(false);
      setUsersLoaded(true);
    }
  }, [serverName, isBeheerder, getAccessTokenSilently]);

  const handleToggleUserAccess = useCallback(async (email: string, currentAccess: boolean) => {
    if (!serverName) return;
    setTogglingUser(email);
    try {
      const token = await getAccessTokenSilently();
      if (currentAccess) {
        await api.removeServerUser(token, serverName, email);
      } else {
        await api.addServerUser(token, serverName, email);
      }
      await loadServerUsers();
      onMutate?.();
    } finally {
      setTogglingUser(null);
    }
  }, [serverName, getAccessTokenSilently, loadServerUsers, onMutate]);

  return { serverUsers, usersLoading, usersLoaded, loadServerUsers, togglingUser, handleToggleUserAccess };
}
